// IM-JRN-016 (IM-UX-146, IM-UX-156..159) — "Acompanhar meu plano": Atualizar realizado → Comparar → Entender desvios →
// Corrigir rota. Disponível com plano aprovado; antes disso, "Aguardando plano" com o caminho para planejar. Poucos números,
// todos com ação: meta e realizado do mês, acumulado, projeção explicada; fatos e hipóteses separados; correção nunca
// reescreve o plano original.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ACTION_STATE_LABEL, fmtBRL, fmtPct, MONTH_NAME, objectiveOf, REVIEW_DECISION_LABEL, REVIEW_DECISIONS, TRACK_STATUS_LABEL, type MacroState,
} from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { planningApi, type TrackingViewDto } from '../data/planning';
import { useResource } from '../hooks/useResource';
import { ObjectiveFlow, type FlowPhase } from './ObjectiveFlow';
import { ObjectiveHead, usePhase } from './ObjectiveWorkspace';
import { PlanChart } from './PlanChart';

const KEYS = ['realizado', 'comparar', 'desvios', 'corrigir'] as const;
const TONE: Record<string, string> = { ACIMA: 'mint', NO_RITMO: 'mint', ATENCAO: 'amber', FORA_DO_RITMO: 'critical', SEM_DADOS: 'gray' };
const errMsg = (e: unknown, f: string) => (e instanceof ApiError ? e.message : f);

export function TrackObjective() {
  const [params] = useSearchParams();
  const decisionId = params.get('decisionId');
  const def = objectiveOf('acompanhar-plano')!;
  const { state, reload, refresh } = useResource(async (s) => {
    const ov = await planningApi.overview(s);
    const t = ov.tracking ? await planningApi.tracking(ov.tracking.planId, null, s) : null;
    return { ov, t };
  }, []);
  return (
    <section aria-labelledby="page-title">
      <ObjectiveHead def={def} journey={null} hideAnalyses>
        <Link className="secondary btn-link" to="/objetivos/acompanhar-plano/integracoes">Integrações e atualização automática</Link>
      </ObjectiveHead>
      <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
        {(d) => d.t ? <TrackFlow t={d.t} refresh={refresh} decisionId={decisionId} /> : (
          <div className="xp-shell">
            <EmptyState title="Aguardando plano" action={<Link className="cta btn-link" to="/objetivos/planejar">Planejar meu próximo ano</Link>}>
              O acompanhamento começa quando houver um plano aprovado. {d.ov.plan ? `O plano de ${d.ov.plan.year} está ${d.ov.plan.status ? `em ${d.ov.plan.status === 'EM_REVISAO' ? 'revisão' : 'rascunho'}` : 'em preparação'}.` : 'Comece pelo plano do próximo ano.'}
            </EmptyState>
          </div>
        )}
      </AsyncView>
    </section>
  );
}

function TrackFlow({ t, refresh, decisionId }: { t: TrackingViewDto; refresh: () => void; decisionId: string | null }) {
  const def = objectiveOf('acompanhar-plano')!;
  const tr = t.tracking;
  const openActions = t.actions.filter((a) => a.state === 'ABERTA' || a.state === 'EM_ANDAMENTO');
  const states: Record<(typeof KEYS)[number], MacroState> = {
    realizado: t.dueMonth ? 'ATUAL' : tr.lastMonth ? 'CONCLUIDA' : 'ATUAL',
    comparar: tr.lastMonth ? 'CONCLUIDA' : 'AGUARDANDO_FASE_ANTERIOR',
    desvios: !tr.lastMonth ? 'AGUARDANDO_FASE_ANTERIOR' : t.diagnosis.evidences.length ? 'AGUARDANDO_REVISAO' : 'CONCLUIDA',
    corrigir: !tr.lastMonth ? 'AGUARDANDO_FASE_ANTERIOR' : t.reviewDue || openActions.length ? 'AGUARDANDO_DECISAO' : 'CONTINUA',
  };
  const first = KEYS.find((k) => states[k] !== 'CONCLUIDA' && states[k] !== 'CONTINUA') ?? 'comparar';
  const [selected, select] = usePhase(KEYS, first);
  const flow: FlowPhase[] = def.phases.map((p) => ({ key: p.key, name: p.name, question: p.question, state: states[p.key as (typeof KEYS)[number]], current: p.key === first,
    count: p.key === 'desvios' && t.diagnosis.evidences.length ? `${t.diagnosis.evidences.length} ${t.diagnosis.evidences.length === 1 ? 'fato' : 'fatos'}` : p.key === 'corrigir' && openActions.length ? `${openActions.length} ${openActions.length === 1 ? 'ação aberta' : 'ações abertas'}` : null }));
  return (
    <div className="xp-shell">
      <div className="xp-overview">
        <div><b>Plano {t.year} · versão {t.version.versionNo} aprovada · <span className={`tag ${TONE[tr.status]}`}>{TRACK_STATUS_LABEL[tr.status]}</span></b>
          <span>{tr.ytd ? `Até ${MONTH_NAME[tr.lastMonth! - 1]}: ${fmtBRL(tr.ytd.actual)} realizados de ${fmtBRL(tr.ytd.target)} previstos (${fmtPct(tr.ytd.deviationPct, true)}).` : 'Ainda não há realizado registrado neste ano.'} Meta anual {fmtBRL(tr.annualTarget)}.</span></div>
      </div>
      <ObjectiveFlow id="xp-track" label="Fases do acompanhamento" phases={flow} selected={selected} onSelect={select}>
        {selected === 'realizado' && <ActualsPhase t={t} onSaved={refresh} />}
        {selected === 'comparar' && <ComparePhase t={t} />}
        {selected === 'desvios' && <DeviationPhase t={t} next={() => select('corrigir')} />}
        {selected === 'corrigir' && <CorrectPhase t={t} onSaved={refresh} decisionId={decisionId} />}
      </ObjectiveFlow>
    </div>
  );
}

function ActualsPhase({ t, onSaved }: { t: TrackingViewDto; onSaved: () => void }) {
  const toast = useToast();
  const month = t.dueMonth ?? t.tracking.lastMonth ?? 1;
  const cur = t.tracking.months.find((m) => m.month === month)!;
  const [rev, setRev] = useState(cur.actual === null ? '' : String(cur.actual));
  const [exp, setExp] = useState(cur.expensesActual === null ? '' : String(cur.expensesActual));
  const [contracts, setContracts] = useState('');
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const save = async () => {
    setBusy(true); setIssues({});
    try {
      const r = await planningApi.save({ dataset: 'REALIZADO', strategy: 'ATUALIZAR', apply: true, rows: [{ line: 1, values: { year: t.year, month, revenue: rev || null, operatingExpenses: exp || null, newContracts: contracts || null } }] });
      if (r.errors) { setIssues(Object.fromEntries(r.issues.filter((i) => i.severity === 'ERRO').map((i) => [i.column ?? '', i.message]))); return; }
      toast(`Realizado de ${MONTH_NAME[month - 1]} registrado.`); onSaved();
    } catch (e) {
      const det = e instanceof ApiError ? (e.details as { issues?: Array<{ column: string | null; message: string; severity: string }> }) : undefined;
      if (det?.issues) setIssues(Object.fromEntries(det.issues.filter((i) => i.severity === 'ERRO').map((i) => [i.column ?? '', i.message])));
      toast(errMsg(e, 'Não foi possível registrar.'), 'error');
    } finally { setBusy(false); }
  };
  const err = (col: string, id: string) => issues[col] ? <small id={id} className="field-error">{issues[col]}</small> : null;
  return (
    <>
      <article className="xp-panel" aria-labelledby="tr-act-h">
        <span className="xp-kicker">Fase 1 de 4 · Atualizar realizado</span>
        <h2 id="tr-act-h">{t.dueMonth ? `Registre o realizado de ${MONTH_NAME[month - 1]}` : `Realizado em dia até ${MONTH_NAME[month - 1]}`}</h2>
        <p>Meta de {MONTH_NAME[month - 1]}: {fmtBRL(cur.target)} de faturamento e {fmtBRL(cur.expensesTarget)} de despesas.</p>
        <div className="pl-form">
          <label className="field">Faturamento de {MONTH_NAME[month - 1]} (R$)
            <input inputMode="decimal" value={rev} onChange={(e) => setRev(e.target.value)} aria-invalid={!!issues.Faturamento} aria-describedby={issues.Faturamento ? 'tr-e-rev' : undefined} />{err('Faturamento', 'tr-e-rev')}</label>
          <label className="field">Despesas operacionais (R$)
            <input inputMode="decimal" value={exp} onChange={(e) => setExp(e.target.value)} aria-invalid={!!issues['Despesas operacionais totais']} aria-describedby={issues['Despesas operacionais totais'] ? 'tr-e-exp' : undefined} />{err('Despesas operacionais totais', 'tr-e-exp')}</label>
          <label className="field">Novos contratos (opcional)
            <input inputMode="numeric" value={contracts} onChange={(e) => setContracts(e.target.value)} /></label>
        </div>
        <div className="xp-cta-row">
          <button type="button" className="xp-primary petrol" disabled={busy || !rev || !exp} onClick={save}>{busy ? 'Registrando…' : `Registrar ${MONTH_NAME[month - 1]}`}</button>
          <Link className="secondary btn-link" to="/objetivos/planejar/dados?aba=mensal">Grade mensal</Link>
          <Link className="secondary btn-link" to="/objetivos/planejar/dados?aba=planilha">Enviar planilha</Link>
        </div>
      </article>
      <aside className="xp-panel" aria-labelledby="tr-src-h">
        <span className="xp-kicker">Outras formas</span>
        <h3 id="tr-src-h">De onde vem o realizado</h3>
        <ul className="plain-list"><li>Grade mensal ou planilha, a qualquer momento.</li><li>Planilha publicada lida automaticamente (diária, semanal ou mensal).</li><li>Envio direto do seu sistema financeiro.</li></ul>
        <Link className="link-btn" to="/objetivos/acompanhar-plano/integracoes">Configurar atualização automática</Link>
      </aside>
    </>
  );
}

function ComparePhase({ t }: { t: TrackingViewDto }) {
  const tr = t.tracking;
  const last = tr.lastMonth ? tr.months[tr.lastMonth - 1]! : null;
  return (
    <article className="xp-panel pl-wide" aria-labelledby="tr-cmp-h">
      <span className="xp-kicker">Fase 2 de 4 · Comparar</span>
      <h2 id="tr-cmp-h">{tr.ytd ? `${TRACK_STATUS_LABEL[tr.status]}: ${fmtPct(tr.ytd.deviationPct, true)} no acumulado` : 'Sem realizado para comparar'}</h2>
      {tr.ytd && last && (
        <ul className="pl-facts pl-facts-6">
          <li><b>{fmtBRL(last.target)}</b><span>Meta de {MONTH_NAME[last.month - 1]}</span></li>
          <li><b>{fmtBRL(last.actual)}</b><span>Realizado · {fmtBRL(last.deviation)} ({fmtPct(last.deviationPct, true)})</span></li>
          <li><b>{fmtBRL(tr.ytd.actual)}</b><span>Acumulado realizado · previsto {fmtBRL(tr.ytd.target)}</span></li>
          <li><b>{fmtBRL(tr.projection.value)}</b><span>Projeção de fechamento · {fmtPct(tr.projection.vsTargetPct, true)} da meta</span></li>
          <li><b>{fmtBRL(tr.ytd.expensesActual)}</b><span>Despesas · previstas {fmtBRL(tr.ytd.expensesTarget)} ({fmtPct(tr.ytd.expensesDeviationPct, true)})</span></li>
          <li><b>{t.actions.filter((a) => a.state === 'ABERTA' || a.state === 'EM_ANDAMENTO').length}</b><span>Ações pendentes</span></li>
        </ul>
      )}
      <p className="cell-sub">Projeção: {tr.projection.formula}.{tr.missingMonths.length ? ` Sem realizado em ${tr.missingMonths.map((m) => MONTH_NAME[m - 1]).join(', ')} (não conta como zero).` : ''}</p>
      <PlanChart months={tr.months} />
      {t.offerings.some((o) => o.actualYtd !== null) && (
        <><h3>Principais ofertas</h3><ul className="xp-steps-detail">{t.offerings.map((o) => <li key={o.offeringId}><span>{o.name}</span><span>{fmtBRL(o.actualYtd)} de {fmtBRL(o.targetYtd)} previstos até agora</span></li>)}</ul></>
      )}
      {t.crm && <p className="pl-note"><b>Vendas no CRM em {t.year}:</b> {t.crm.won} ganhas ({fmtBRL(t.crm.wonAmount)}), {t.crm.open} em aberto ({fmtBRL(t.crm.openAmount)}){t.crm.winRatePct !== null ? `, conversão ${fmtPct(t.crm.winRatePct)}` : ''}. Venda fechada não é faturamento: o realizado acima vem da base financeira.</p>}
    </article>
  );
}

function DeviationPhase({ t, next }: { t: TrackingViewDto; next: () => void }) {
  const d = t.diagnosis;
  return (
    <>
      <article className="xp-panel" aria-labelledby="tr-dev-h">
        <span className="xp-kicker">Fase 3 de 4 · Entender desvios</span>
        <h2 id="tr-dev-h">{d.summary}</h2>
        <h3>Fatos observados</h3>
        {d.evidences.length ? <ul className="xp-list" role="list">{d.evidences.map((e) => <li key={e.key} className="xp-row"><span className={`tag ${e.severity === 'ALTA' ? 'critical' : 'amber'}`}>Fato</span><div className="xp-row-main"><b>{e.text}</b></div></li>)}</ul> : <p className="xp-quiet">Nenhum desvio relevante nos números.</p>}
        {d.hypotheses.length > 0 && <>
          <h3>Hipóteses a verificar</h3>
          <p className="cell-sub">Possíveis causas — não comprovadas. Confirme com a equipe antes de agir.</p>
          <ul className="xp-list" role="list">{d.hypotheses.map((h) => <li key={h.key} className="xp-row"><span className="tag gray">Hipótese</span><div className="xp-row-main"><b>{h.text}</b></div></li>)}</ul>
        </>}
        <div className="xp-cta-row"><button type="button" className="xp-primary petrol" onClick={next}>Decidir a correção</button></div>
      </article>
      <aside className="xp-panel"><span className="xp-kicker">Como o InteliMove separa</span><h3>Fato × hipótese</h3><p className="cell-sub">Fato é o que os números mostram (realizado, metas, CRM). Hipótese é uma explicação possível, sempre marcada como tal. Recomendações dizem em qual dos dois se apoiam.</p></aside>
    </>
  );
}

function CorrectPhase({ t, onSaved, decisionId }: { t: TrackingViewDto; onSaved: () => void; decisionId: string | null }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(t.tracking.lastMonth ?? 1);
  const [decision, setDecision] = useState<(typeof REVIEW_DECISIONS)[number]>('MANTER');
  const [comment, setComment] = useState('');
  const run = async (fn: () => Promise<unknown>, ok: string) => { setBusy(true); try { await fn(); toast(ok); onSaved(); } catch (e) { toast(errMsg(e, 'Não foi possível concluir.'), 'error'); } finally { setBusy(false); } };
  const open = t.actions.filter((a) => a.state === 'ABERTA' || a.state === 'EM_ANDAMENTO');
  const done = t.actions.filter((a) => a.state === 'CONCLUIDA' || a.state === 'CANCELADA');
  return (
    <>
      <article className="xp-panel" aria-labelledby="tr-cor-h">
        <span className="xp-kicker">Fase 4 de 4 · Corrigir rota</span>
        <h2 id="tr-cor-h">{t.reviewDue ? `Revise ${MONTH_NAME[(t.tracking.lastMonth ?? 1) - 1]} e decida` : 'O que fazer agora'}</h2>
        {decisionId && <p className="pl-note"><b>Decisão selecionada:</b> a ação registrada nesta visita será vinculada ao histórico dessa decisão.</p>}
        {t.diagnosis.recommendations.length > 0 && <>
          <h3>Recomendações</h3>
          <ul className="xp-list" role="list">{t.diagnosis.recommendations.map((r) => (
            <li key={r.key} className="xp-row">
              <div className="xp-row-main"><b>{r.title}</b><span>{r.reason} Impacto esperado: {r.impact}</span><span>Urgência {r.urgency.toLowerCase()} · apoiada em {r.basis === 'EVIDENCIA' ? 'fato observado' : 'hipótese'}</span></div>
              <div className="xp-row-side">
                {r.route && <Link className="link-btn" to={r.route}>Abrir</Link>}
                <button type="button" className="secondary" disabled={busy || t.actions.some((a) => a.catalogKey === r.key.split(':')[0] && a.title === r.title && (a.state === 'ABERTA' || a.state === 'EM_ANDAMENTO'))}
                  onClick={() => run(() => planningApi.addAction(t.planId, { title: r.title, reason: r.reason, expectedImpact: r.impact, urgency: r.urgency, basis: decisionId ? 'DECISAO' : r.basis, catalogKey: r.key.split(':')[0]!.replace(/[^A-Z0-9_]/g, '_').slice(0, 60), ...(decisionId ? { decisionId } : {}) }), 'Ação registrada.')}>Registrar ação</button>
              </div>
            </li>))}</ul>
        </>}
        <h3>Ações em andamento</h3>
        {open.length ? <ul className="xp-list" role="list">{open.map((a) => (
          <li key={a.id} className="xp-row">
            <div className="xp-row-main"><b>{a.title}</b><span>{a.reason}</span><span>{a.ownerName ? `Responsável: ${a.ownerName}` : 'Sem responsável'}{a.dueDate ? ` · prazo ${new Date(`${a.dueDate}T12:00:00`).toLocaleDateString('pt-BR')}` : ''} · {a.basis === 'HIPOTESE' ? 'apoiada em hipótese' : 'apoiada em fato'}</span></div>
            <div className="xp-row-side">
              <label className="sr-only" htmlFor={`act-${a.id}`}>Estado de {a.title}</label>
              <select id={`act-${a.id}`} value={a.state} disabled={busy} onChange={(e) => run(() => planningApi.updateAction(a.id, { version: a.version, state: e.target.value }), 'Ação atualizada.')}>
                {Object.entries(ACTION_STATE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </li>))}</ul> : <p className="xp-quiet">Nenhuma ação aberta.</p>}
        <form className="pl-review" onSubmit={(e) => { e.preventDefault(); void (async () => {
          setBusy(true);
          try {
            const r = await planningApi.review(t.planId, { month, decision, comment: comment.trim() || undefined });
            toast(r.newVersionId ? 'Revisão registrada. Uma nova versão do plano foi aberta em rascunho; o plano aprovado continua valendo.' : 'Revisão do mês registrada.');
            setComment('');
            if (r.newVersionId) navigate(`/objetivos/planejar?ano=${t.year}&fase=metas`); else onSaved();
          } catch (err) { toast(errMsg(err, 'Não foi possível registrar a revisão.'), 'error'); } finally { setBusy(false); }
        })(); }}>
          <h3>Revisão do mês</h3>
          <label className="field">Mês
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>{t.tracking.months.filter((m) => m.actual !== null).map((m) => <option key={m.month} value={m.month}>{MONTH_NAME[m.month - 1]}</option>)}</select>
          </label>
          <fieldset className="field pl-radio"><legend>Decisão</legend>
            {REVIEW_DECISIONS.map((k) => <label key={k}><input type="radio" name="tr-dec" checked={decision === k} onChange={() => setDecision(k)} /> {REVIEW_DECISION_LABEL[k]}</label>)}
          </fieldset>
          <label className="field wide">Comentário
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </label>
          {(decision === 'REVISAR_METAS' || decision === 'NOVA_VERSAO') && <p className="cell-sub">Abre uma nova versão do plano em rascunho{decision === 'REVISAR_METAS' ? ' com os meses já realizados preservados' : ''}. A versão aprovada continua sendo a referência até a nova ser aprovada.</p>}
          <button type="submit" className="xp-primary petrol" disabled={busy || t.tracking.lastMonth === null}>Registrar a revisão</button>
        </form>
      </article>
      <aside className="xp-panel" aria-labelledby="tr-hist-h">
        <span className="xp-kicker">Histórico</span>
        <h3 id="tr-hist-h">Revisões registradas</h3>
        {t.reviews.length ? <ul className="xp-steps-detail">{t.reviews.map((r) => <li key={r.id}><span><b>{MONTH_NAME[r.month - 1]}</b> · {r.decisionLabel}{r.comment ? <span className="cell-sub"> · {r.comment}</span> : null}<br /><span className="cell-sub">{r.by ?? '—'} · {new Date(r.at).toLocaleDateString('pt-BR')} · versão {r.versionNo}</span></span><span className={`tag ${TONE[r.situation]}`}>{TRACK_STATUS_LABEL[r.situation as keyof typeof TRACK_STATUS_LABEL]}</span></li>)}</ul> : <p className="xp-quiet">Nenhuma revisão ainda.</p>}
        {done.length > 0 && <><h4 className="pl-h4">Ações encerradas</h4><ul className="plain-list">{done.slice(0, 6).map((a) => <li key={a.id}>{a.title} <span className="cell-sub">· {ACTION_STATE_LABEL[a.state as keyof typeof ACTION_STATE_LABEL]}</span></li>)}</ul></>}
      </aside>
    </>
  );
}
